import { useEffect, useState, type FormEvent } from 'react';
import { LLM_MODELS, type GeneratedPlan, type LlmProviderId } from '@verifyloop/core';
import { describeError } from '../api/client';
import { generateTestPlan } from '../api/endpoints';
import { CUSTOM_MODEL_OPTION, modelSelection, resolveModelSelectValue } from '../lib/modelSelect';
import { ErrorNotice } from './Feedback';
import { CheckboxField, SelectField, TextArea, TextField } from './Form';
import { useToast } from '../state';

export interface ByokSettings {
  provider: LlmProviderId;
  apiKey: string;
  model: string;
  baseUrl?: string;
  saveLocally: boolean;
}

const STORAGE_KEY = 'verifyloop.byok';

export function loadSavedByok(): ByokSettings {
  if (typeof window === 'undefined') {
    return { provider: 'anthropic', apiKey: '', model: 'claude-3-5-sonnet-20241022', saveLocally: false };
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ByokSettings>;
      return {
        provider: parsed.provider ?? 'anthropic',
        apiKey: parsed.apiKey ?? '',
        model: parsed.model ?? 'claude-3-5-sonnet-20241022',
        baseUrl: parsed.baseUrl ?? '',
        saveLocally: true,
      };
    }
  } catch {
    /* ignore */
  }
  return { provider: 'anthropic', apiKey: '', model: 'claude-3-5-sonnet-20241022', saveLocally: false };
}

export function saveByokLocally(settings: ByokSettings): void {
  if (typeof window === 'undefined') return;
  try {
    if (settings.saveLocally && settings.apiKey.trim()) {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          provider: settings.provider,
          apiKey: settings.apiKey.trim(),
          model: settings.model.trim(),
          baseUrl: settings.baseUrl?.trim() || undefined,
        }),
      );
    } else {
      window.localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

interface GenerateTestModalProps {
  projectId: string;
  onClose: () => void;
  onPlanGenerated: (plan: GeneratedPlan['plan']) => void;
}

const PROVIDER_OPTIONS = [
  { value: 'anthropic', label: 'Anthropic (Claude)' },
  { value: 'openai', label: 'OpenAI (GPT)' },
  { value: 'google', label: 'Google (Gemini)' },
  { value: 'custom', label: 'Custom (OpenAI-compatible)' },
] as const;

const MODEL_DEFAULTS: Record<LlmProviderId, string> = {
  anthropic: 'claude-3-5-sonnet-20241022',
  openai: 'gpt-4o-mini',
  google: 'gemini-1.5-flash-latest',
  custom: 'llama-3.3-70b-versatile',
};

const SAMPLE_PRDS = [
  {
    label: 'User Login & Password Validation',
    text: `As a registered user, I want to sign in on the /login page.
- Entering an empty password should show an error "Password is required".
- Entering invalid credentials should show "Invalid email or password".
- Entering correct credentials should navigate to /dashboard and show "Welcome back".`,
  },
  {
    label: 'Shopping Cart & Discount Checkout',
    text: `Checkout flow for the shopping cart:
- Navigate to /cart and click the Checkout button.
- Fill in shipping address with valid details.
- Apply promo code "SAVE20" and verify price discount is applied.
- Submit order and verify confirmation screen says "Order Placed Successfully".`,
  },
  {
    label: 'Profile Settings & Dark Mode Toggle',
    text: `User settings page verification:
- Navigate to /settings.
- Click on the Theme toggle switch.
- Verify the document data-theme attribute updates to "dark".
- Refresh page and verify dark theme persists.`,
  },
];

export function GenerateTestModal({
  projectId,
  onClose,
  onPlanGenerated,
}: GenerateTestModalProps): JSX.Element {
  const toast = useToast();
  const [mode, setMode] = useState<'byok' | 'hosted'>('byok');
  const [source, setSource] = useState('');
  const [hint, setHint] = useState('');
  const [provider, setProvider] = useState<LlmProviderId>('anthropic');
  const [model, setModel] = useState<string>('claude-3-5-sonnet-20241022');
  const [customModel, setCustomModel] = useState(false);
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [saveLocally, setSaveLocally] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Load saved BYOK credentials on mount
  useEffect(() => {
    const saved = loadSavedByok();
    if (saved.apiKey) {
      setProvider(saved.provider);
      setModel(saved.model || MODEL_DEFAULTS[saved.provider]);
      setBaseUrl(saved.baseUrl || '');
      setApiKey(saved.apiKey);
      setSaveLocally(true);
      setMode('byok');
    }
  }, []);

  const currentModels = LLM_MODELS[provider] ?? LLM_MODELS.anthropic;
  const modelOptions = [
    ...currentModels.map((m) => ({ value: m.value, label: m.label })),
    { value: CUSTOM_MODEL_OPTION, label: 'Other / Custom Model Name...' },
  ];
  const selectValue = resolveModelSelectValue(currentModels, model, customModel);

  const handleProviderChange = (next: string) => {
    const p = next as LlmProviderId;
    setProvider(p);
    const def = LLM_MODELS[p]?.[0]?.value ?? '';
    setModel(def);
    setCustomModel(false);
  };

  const handleModelChange = (value: string) => {
    const selection = modelSelection(value);
    setCustomModel(selection.isCustom);
    setModel(selection.model);
  };

  const handleGenerate = async (e: FormEvent) => {
    e.preventDefault();
    if (!source.trim()) {
      setError(new Error('Please provide a feature description or PRD requirements.'));
      return;
    }
    if (mode === 'byok' && !apiKey.trim()) {
      setError(new Error('Please enter your API key or switch to the Hosted Model.'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await generateTestPlan({
        projectId,
        source: source.trim(),
        hint: hint.trim() || undefined,
        ...(mode === 'byok'
          ? {
              provider,
              model: model.trim() || undefined,
              baseUrl: baseUrl.trim() || undefined,
              apiKey: apiKey.trim(),
            }
          : {}),
      });

      // Save BYOK locally if requested
      if (mode === 'byok') {
        saveByokLocally({
          provider,
          model,
          baseUrl,
          apiKey,
          saveLocally,
        });
      }

      toast.success(
        `Generated plan "${result.plan.name}" with ${result.plan.planSteps.length} steps (${
          result.servedBy === 'byok' ? 'BYOK · 0 credits' : 'Hosted · 2 credits'
        })`,
      );

      onPlanGenerated(result.plan);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(describeError(err)));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal modal--wide"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: '780px' }}
      >
        <div className="modal__header">
          <div>
            <h2 className="modal__title">✨ Generate Test Plan with AI</h2>
            <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
              Turn PRD specs or user stories into deterministic Justloop test steps.
            </p>
          </div>
          <button type="button" className="btn btn--small btn--ghost" onClick={onClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleGenerate}>
          <div className="modal__body" style={{ maxHeight: '72vh', overflowY: 'auto' }}>
            {error ? <ErrorNotice error={error} /> : null}

            {/* Mode Selector */}
            <div className="admin-split-legend" style={{ marginBottom: '16px', gap: '12px' }}>
              <button
                type="button"
                className={`btn btn--small ${mode === 'byok' ? '' : 'btn--secondary'}`}
                onClick={() => setMode('byok')}
                style={{ flex: 1, padding: '10px' }}
              >
                <strong>🔑 Bring Your Own Key (BYOK)</strong>
                <div style={{ fontSize: '11px', opacity: 0.85 }}>Free (0 credits) · Private to your browser</div>
              </button>
              <button
                type="button"
                className={`btn btn--small ${mode === 'hosted' ? '' : 'btn--secondary'}`}
                onClick={() => setMode('hosted')}
                style={{ flex: 1, padding: '10px' }}
              >
                <strong>⚡ Hosted Model (Justloop)</strong>
                <div style={{ fontSize: '11px', opacity: 0.85 }}>Costs 2 credits (~$0.057) · No API key needed</div>
              </button>
            </div>

            {/* BYOK Configuration Form */}
            {mode === 'byok' ? (
              <div
                style={{
                  padding: '16px',
                  background: 'var(--bg-inset)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  marginBottom: '16px',
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <SelectField
                    label="AI Provider"
                    value={provider}
                    onChange={handleProviderChange}
                    options={PROVIDER_OPTIONS}
                  />
                  <SelectField
                    label="AI Model"
                    value={selectValue}
                    onChange={handleModelChange}
                    options={modelOptions}
                  />
                </div>

                {selectValue === '__custom__' ? (
                  <div style={{ marginTop: '10px' }}>
                    <TextField
                      label="Specify Custom Model Name"
                      value={model}
                      onChange={setModel}
                      placeholder="e.g. meta-llama/Llama-3.3-70B-Instruct"
                      hint="Enter the exact model identifier accepted by your provider or proxy endpoint."
                      required
                    />
                  </div>
                ) : null}

                <div style={{ marginTop: '10px' }}>
                  <TextField
                    label="Your API Key (BYOK)"
                    type="password"
                    value={apiKey}
                    onChange={setApiKey}
                    placeholder="sk-ant-... or sk-proj-... or AI key"
                    autoComplete="new-password"
                    hint="Your secret key is never stored on our database. It is sent per-request to generate the plan and discarded immediately."
                    required
                  />
                </div>

                <div style={{ marginTop: '10px' }}>
                  <TextField
                    label="Base URL (Optional)"
                    value={baseUrl}
                    onChange={setBaseUrl}
                    placeholder={provider === 'custom' ? 'https://api.together.xyz/v1' : 'Provider default'}
                    hint="Optional proxy or OpenAI-compatible endpoint."
                  />
                </div>

                <div style={{ marginTop: '10px' }}>
                  <CheckboxField
                    label="Remember this key in my browser for future test generations"
                    checked={saveLocally}
                    onChange={setSaveLocally}
                    hint="Stored only in your browser's localStorage. Never uploaded to the server."
                  />
                </div>
              </div>
            ) : (
              <div
                style={{
                  padding: '14px 16px',
                  background: 'var(--bg-panel)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  marginBottom: '16px',
                  fontSize: '0.88rem',
                }}
              >
                <p style={{ margin: '0 0 6px' }}>
                  Using Justloop's <strong>Hosted Operator Model</strong>.
                </p>
                <p className="muted" style={{ margin: 0, fontSize: '0.82rem' }}>
                  A charge of <strong>2.0 credits</strong> will be deducted from your workspace balance upon successful draft generation.
                </p>
              </div>
            )}

            {/* Source PRD / Requirements */}
            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                <label className="field__label" style={{ margin: 0 }}>
                  Feature Description / PRD Notes <span style={{ color: 'var(--fail)' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: '6px', fontSize: '11px' }}>
                  <span className="muted">Sample templates:</span>
                  {SAMPLE_PRDS.map((s, idx) => (
                    <button
                      key={s.label}
                      type="button"
                      className="linkish"
                      style={{ fontSize: '11px' }}
                      onClick={() => setSource(s.text)}
                    >
                      #{idx + 1}
                    </button>
                  ))}
                </div>
              </div>
              <TextArea
                label=""
                value={source}
                onChange={setSource}
                rows={5}
                placeholder="Paste your PRD user stories, acceptance criteria, or natural-language description of the user flow..."
              />
            </div>

            {/* Optional Guidance Hint */}
            <TextField
              label="Additional Guidance / Edge Case Focus (Optional)"
              value={hint}
              onChange={setHint}
              placeholder="e.g. Focus on edge cases, form field validation, and error messages"
            />
          </div>

          <div className="modal__footer">
            <button type="button" className="btn btn--secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn" disabled={loading || !source.trim()}>
              {loading ? 'Generating Plan Steps with AI...' : '✨ Generate Test Plan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
